import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';
import { fmt } from '../utils/gst';
import toast from 'react-hot-toast';

const PAY_METHODS = ['bank','upi','cash','credit-card','cheque'];

function monthLabel(m) {
  return new Date(m + '-01').toLocaleString('default', { month: 'long', year: 'numeric' });
}

// ── Add / Edit expense modal ────────────────────────────────────────────────
function ExpenseModal({ expense, categories, onClose, onSave, onSaveAndRecur }) {
  const now = new Date().toISOString().slice(0,10);
  const [form, setForm] = useState({
    category_id: expense?.category_id || '',
    name:         expense?.name || '',
    amount:       expense?.amount || '',
    gst_amount:   expense?.gst_amount || '',
    expense_date: expense?.expense_date?.slice(0,10) || now,
    payment_method: expense?.payment_method || 'bank',
    vendor:       expense?.vendor || '',
    notes:        expense?.notes || '',
  });

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.amount || !form.expense_date) return;
    onSave(form);
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ width: 480 }}>
        <div className="modal-head">
          <h3>{expense ? 'Edit Expense' : 'Add Expense'}</h3>
          <button className="btn-ghost" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 0' }}>
          <div className="form-row">
            <label className="form-label">Category</label>
            <select className="form-input" value={form.category_id} onChange={e => upd('category_id', e.target.value)}>
              <option value="">— Uncategorised —</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
            </select>
          </div>
          <div className="form-row">
            <label className="form-label">Description *</label>
            <input className="form-input" value={form.name} onChange={e => upd('name', e.target.value)} placeholder="e.g. Adobe Creative Cloud" required />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-row">
              <label className="form-label">Amount (₹) *</label>
              <input className="form-input" type="number" step="0.01" min="0" value={form.amount} onChange={e => upd('amount', e.target.value)} placeholder="0.00" required />
            </div>
            <div className="form-row">
              <label className="form-label">GST paid (ITC) ₹</label>
              <input className="form-input" type="number" step="0.01" min="0" value={form.gst_amount} onChange={e => upd('gst_amount', e.target.value)} placeholder="0.00" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-row">
              <label className="form-label">Date *</label>
              <input className="form-input" type="date" value={form.expense_date} onChange={e => upd('expense_date', e.target.value)} required />
            </div>
            <div className="form-row">
              <label className="form-label">Payment method</label>
              <select className="form-input" value={form.payment_method} onChange={e => upd('payment_method', e.target.value)}>
                {PAY_METHODS.map(m => <option key={m} value={m}>{m.replace('-',' ')}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <label className="form-label">Vendor / Payee</label>
            <input className="form-input" value={form.vendor} onChange={e => upd('vendor', e.target.value)} placeholder="e.g. Adobe Inc" />
          </div>
          <div className="form-row">
            <label className="form-label">Notes</label>
            <input className="form-input" value={form.notes} onChange={e => upd('notes', e.target.value)} placeholder="Optional" />
          </div>
          {form.gst_amount > 0 && form.amount > 0 && (
            <div style={{ fontSize: 12, color: '#1E7E4E', background: '#F0FFF4', padding: '8px 12px', borderRadius: 6 }}>
              ITC of {fmt(parseFloat(form.gst_amount))} will be credited against your GST output tax
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4, flexWrap: 'wrap' }}>
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            {onSaveAndRecur && (
              <button type="button" className="btn-ghost" style={{ color: 'var(--primary)', border: '1px solid var(--primary)' }}
                onClick={() => {
                  if (!form.name || !form.amount || !form.expense_date) return;
                  onSaveAndRecur(form);
                }}>
                + Also add to Recurring
              </button>
            )}
            <button type="submit" className="btn-primary">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Recurring template modal ────────────────────────────────────────────────
function RecurringModal({ template, categories, onClose, onSave }) {
  const thisMonth = new Date().toISOString().slice(0,7);
  const [form, setForm] = useState({
    category_id: template?.category_id || '',
    name:         template?.name || '',
    amount:       template?.amount || '',
    gst_amount:   template?.gst_amount || '',
    frequency:    template?.frequency || 'monthly',
    start_month:  template?.start_month || thisMonth,
    end_month:    template?.end_month || '',
    payment_method: template?.payment_method || 'bank',
    vendor:       template?.vendor || '',
  });
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ width: 460 }}>
        <div className="modal-head">
          <h3>{template ? 'Edit Recurring' : 'Add Recurring Expense'}</h3>
          <button className="btn-ghost" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={e => { e.preventDefault(); onSave(form); }} style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 0' }}>
          <div className="form-row">
            <label className="form-label">Category</label>
            <select className="form-input" value={form.category_id} onChange={e => upd('category_id', e.target.value)}>
              <option value="">— Uncategorised —</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
            </select>
          </div>
          <div className="form-row">
            <label className="form-label">Name *</label>
            <input className="form-input" value={form.name} onChange={e => upd('name', e.target.value)} required placeholder="e.g. Adobe CC Monthly" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-row">
              <label className="form-label">Amount (₹) *</label>
              <input className="form-input" type="number" step="0.01" min="0" value={form.amount} onChange={e => upd('amount', e.target.value)} required />
            </div>
            <div className="form-row">
              <label className="form-label">GST paid (ITC) ₹</label>
              <input className="form-input" type="number" step="0.01" min="0" value={form.gst_amount} onChange={e => upd('gst_amount', e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-row">
              <label className="form-label">Frequency</label>
              <select className="form-input" value={form.frequency} onChange={e => upd('frequency', e.target.value)}>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
            <div className="form-row">
              <label className="form-label">Payment method</label>
              <select className="form-input" value={form.payment_method} onChange={e => upd('payment_method', e.target.value)}>
                {PAY_METHODS.map(m => <option key={m} value={m}>{m.replace('-',' ')}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-row">
              <label className="form-label">Start month</label>
              <input className="form-input" type="month" value={form.start_month} onChange={e => upd('start_month', e.target.value)} required />
            </div>
            <div className="form-row">
              <label className="form-label">End month (optional)</label>
              <input className="form-input" type="month" value={form.end_month} onChange={e => upd('end_month', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <label className="form-label">Vendor</label>
            <input className="form-input" value={form.vendor} onChange={e => upd('vendor', e.target.value)} placeholder="e.g. Adobe Inc" />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary">Save Template</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────
export default function ExpensesPage() {
  const qc = useQueryClient();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [tab, setTab] = useState('expenses'); // 'expenses' | 'recurring' | 'pl'
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showRecurring, setShowRecurring] = useState(false);
  const [editRecurring, setEditRecurring] = useState(null);
  const [monthPicker, setMonthPicker] = useState(null);
  const [pickedMonths, setPickedMonths] = useState(new Set());
  const [selected, setSelected] = useState(new Set());

  const { data: categories = [] } = useQuery({ queryKey: ['expense-categories'], queryFn: () => api.get('/expenses/categories').then(r => r.data) });
  const { data: expenses = [], isLoading } = useQuery({ queryKey: ['expenses', month], queryFn: () => api.get(`/expenses?month=${month}`).then(r => r.data), enabled: !!month });
  const { data: recurring = [] } = useQuery({ queryKey: ['recurring-expenses'], queryFn: () => api.get('/expenses/recurring').then(r => r.data) });
  const { data: appliedMonths = [] } = useQuery({
    queryKey: ['recurring-applied-months', monthPicker?.id],
    queryFn: () => api.get(`/expenses/recurring/${monthPicker.id}/applied-months`).then(r => r.data),
    enabled: !!monthPicker,
  });

  // Aggregate for P&L tab
  const totalAmount = expenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
  const totalITC    = expenses.reduce((s, e) => s + parseFloat(e.gst_amount || 0), 0);

  // By category breakdown
  const byCat = {};
  expenses.forEach(e => {
    const cat = e.category_name || 'Uncategorised';
    if (!byCat[cat]) byCat[cat] = { icon: e.category_icon || '📦', total: 0, itc: 0 };
    byCat[cat].total += parseFloat(e.amount || 0);
    byCat[cat].itc   += parseFloat(e.gst_amount || 0);
  });

  const addMutation = useMutation({
    mutationFn: data => api.post('/expenses', data),
    onSuccess: () => { qc.invalidateQueries(['expenses', month]); setShowAdd(false); toast.success('Expense added'); },
    onError: () => toast.error('Failed to save'),
  });

  async function handleSaveAndRecur(form) {
    try {
      // Save or update the expense
      if (editing) {
        await api.patch(`/expenses/${editing.id}`, form);
      } else {
        await api.post('/expenses', { ...form });
      }
      // Only create recurring template if one doesn't already exist for this name
      const alreadyRecurring = recurring.some(
        r => r.name.trim().toLowerCase() === form.name.trim().toLowerCase()
      );
      if (alreadyRecurring) {
        toast.success(editing ? 'Expense updated' : 'Expense saved');
        toast('A recurring template for this expense already exists', { icon: 'ℹ️' });
      } else {
        await api.post('/expenses/recurring', {
          category_id: form.category_id,
          name: form.name,
          amount: form.amount,
          gst_amount: form.gst_amount,
          payment_method: form.payment_method,
          vendor: form.vendor,
          frequency: 'monthly',
          start_month: form.expense_date.slice(0, 7),
        });
        toast.success('Expense saved and added to recurring');
      }
      qc.invalidateQueries(['expenses', month]);
      qc.invalidateQueries(['recurring-expenses']);
      setShowAdd(false);
      setEditing(null);
    } catch {
      toast.error('Failed');
    }
  }
  const editMutation = useMutation({
    mutationFn: ({ id, data }) => api.patch(`/expenses/${id}`, data),
    onSuccess: () => { qc.invalidateQueries(['expenses', month]); setEditing(null); toast.success('Updated'); },
    onError: () => toast.error('Failed to update'),
  });
  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/expenses/${id}`),
    onSuccess: () => { qc.invalidateQueries(['expenses', month]); toast.success('Deleted'); },
    onError: () => toast.error('Failed to delete'),
  });
  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids) => { for (const id of ids) await api.delete(`/expenses/${id}`); },
    onSuccess: () => { qc.invalidateQueries(['expenses', month]); setSelected(new Set()); toast.success('Deleted'); },
    onError: () => toast.error('Failed to delete some expenses'),
  });
  const addRecurringMutation = useMutation({
    mutationFn: data => api.post('/expenses/recurring', data),
    onSuccess: () => { qc.invalidateQueries(['recurring-expenses']); setShowRecurring(false); toast.success('Recurring template saved'); },
    onError: () => toast.error('Failed to save'),
  });
  const editRecurringMutation = useMutation({
    mutationFn: ({ id, data }) => api.patch(`/expenses/recurring/${id}`, data),
    onSuccess: () => { qc.invalidateQueries(['recurring-expenses']); setShowRecurring(false); setEditRecurring(null); toast.success('Template updated'); },
    onError: () => toast.error('Failed to update'),
  });
  const deleteRecurringMutation = useMutation({
    mutationFn: id => api.delete(`/expenses/recurring/${id}`),
    onSuccess: () => { qc.invalidateQueries(['recurring-expenses']); toast.success('Template removed'); },
    onError: () => toast.error('Failed'),
  });
  const applyRecurringMutation = useMutation({
    mutationFn: ({ id, month: m }) => api.post(`/expenses/recurring/${id}/apply/${m}`),
    onSuccess: () => { qc.invalidateQueries(['expenses', month]); toast.success('Added to this month'); },
    onError: () => toast.error('Already added or error'),
  });

  // Generate all months from FY start (Apr) through next month
  const fyMonths = (() => {
    const today = new Date();
    const fyStartYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
    const fyStart = new Date(fyStartYear, 3, 1); // April
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 1); // next month
    const months = [];
    for (let d = new Date(fyStart); d <= end; d.setMonth(d.getMonth() + 1))
      months.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
    return months;
  })();

  const openMonthPicker = (r) => {
    setMonthPicker(r);
    setPickedMonths(new Set());
  };

  const applyToPickedMonths = async () => {
    const ms = [...pickedMonths].sort();
    for (const m of ms) {
      await api.post(`/expenses/recurring/${monthPicker.id}/apply/${m}`);
    }
    qc.invalidateQueries(['expenses', month]);
    toast.success(`Added to ${ms.length} month${ms.length > 1 ? 's' : ''}`);
    setMonthPicker(null);
  };

  return (
    <div>
      <div className="section-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2>Expenses & P&L — {monthLabel(month)}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input type="month" className="form-input" value={month} onChange={e => { setMonth(e.target.value); setSelected(new Set()); }} style={{ width: 150 }} />
          <button className="btn-primary" onClick={() => setShowAdd(true)}>+ Add Expense</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        {[['expenses','This Month'], ['pl','P&L Summary'], ['recurring','Recurring']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, fontWeight: tab===k?700:400,
              background: tab===k ? 'var(--accent)' : 'var(--surface)', color: tab===k ? '#fff' : 'var(--text2)', cursor: 'pointer' }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── TAB: Expenses list ─────────────────────────────────── */}
      {tab === 'expenses' && (
        <div>
          {/* Summary bar */}
          {expenses.length > 0 && (
            <div className="card" style={{ padding: '14px 20px', marginBottom: 16, display: 'flex', gap: 24, alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>Total Expenses</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#C0392B' }}>{fmt(totalAmount)}</div>
              </div>
              {totalITC > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>ITC Claimable</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#1E7E4E' }}>{fmt(totalITC)}</div>
                </div>
              )}
              <div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>Net cash out</div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>{fmt(totalAmount - totalITC)}</div>
              </div>
              <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text2)' }}>
                {expenses.length} expense{expenses.length !== 1 ? 's' : ''}
              </div>
            </div>
          )}

          {isLoading && <div className="loading-screen"><div className="spinner" /></div>}

          {!isLoading && expenses.length === 0 && (
            <div className="card empty" style={{ padding: 40, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📊</div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>No expenses logged for {monthLabel(month)}</div>
              <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>Track your business costs here for a complete financial picture</div>
              <button className="btn-primary" onClick={() => setShowAdd(true)}>+ Add first expense</button>
            </div>
          )}

          {expenses.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, padding: '0 4px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text2)', cursor: 'pointer', userSelect: 'none' }}>
                <input type="checkbox"
                  checked={selected.size === expenses.length && expenses.length > 0}
                  onChange={e => setSelected(e.target.checked ? new Set(expenses.map(x => x.id)) : new Set())}
                />
                {selected.size > 0 ? `${selected.size} selected` : 'Select all'}
              </label>
              {selected.size > 0 && (
                <button className="btn-sm" style={{ background: 'var(--red)', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontWeight: 600 }}
                  onClick={() => { if (window.confirm(`Delete ${selected.size} expense${selected.size > 1 ? 's' : ''}?`)) bulkDeleteMutation.mutate([...selected]); }}>
                  Delete {selected.size}
                </button>
              )}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {expenses.map(e => (
              <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={selected.has(e.id)}
                  onChange={ev => { const c = ev.target.checked; setSelected(s => { const n = new Set(s); c ? n.add(e.id) : n.delete(e.id); return n; }); }}
                  style={{ flexShrink: 0, cursor: 'pointer', width: 16, height: 16 }}
                />
                <div className="card" style={{ flex: 1, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, background: selected.has(e.id) ? '#EFF6FF' : undefined }}>
                  <div style={{ fontSize: 20, width: 32, textAlign: 'center', flexShrink: 0 }}>{e.category_icon || '📦'}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{e.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text2)' }}>
                      {e.category_name || 'Uncategorised'} · {e.expense_date?.slice(0,10)} · {e.payment_method}
                      {e.vendor && ` · ${e.vendor}`}
                      {e.notes && ` · ${e.notes}`}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontWeight: 700, color: '#C0392B' }}>{fmt(parseFloat(e.amount))}</div>
                    {parseFloat(e.gst_amount) > 0 && (
                      <div style={{ fontSize: 11, color: '#1E7E4E' }}>ITC: {fmt(parseFloat(e.gst_amount))}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button className="btn-ghost btn-sm" onClick={() => setEditing(e)}>Edit</button>
                    <button className="btn-ghost btn-sm" style={{ color: 'var(--red)' }} onClick={() => { if(window.confirm('Delete this expense?')) deleteMutation.mutate(e.id); }}>Del</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── TAB: P&L Summary ──────────────────────────────────── */}
      {tab === 'pl' && (
        <div>
          <div className="card" style={{ padding: '20px 24px', marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 16 }}>
              Expense Breakdown — {monthLabel(month)}
            </div>
            {Object.keys(byCat).length === 0 && (
              <div style={{ color: 'var(--text2)', fontSize: 13 }}>No expenses this month yet.</div>
            )}
            {Object.entries(byCat).sort((a,b) => b[1].total - a[1].total).map(([cat, data]) => (
              <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 18, width: 28, textAlign: 'center' }}>{data.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{cat}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#C0392B' }}>{fmt(data.total)}</span>
                  </div>
                  <div style={{ height: 5, background: 'var(--surface2)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', background: '#C0392B', opacity: 0.7, borderRadius: 3, width: `${(data.total / totalAmount * 100).toFixed(1)}%`, transition: 'width 0.4s ease' }} />
                  </div>
                  {data.itc > 0 && <div style={{ fontSize: 10, color: '#1E7E4E', marginTop: 2 }}>ITC: {fmt(data.itc)}</div>}
                </div>
              </div>
            ))}
            {totalAmount > 0 && (
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 8, display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 14 }}>
                <span>Total</span>
                <span style={{ color: '#C0392B' }}>{fmt(totalAmount)}</span>
              </div>
            )}
            {totalITC > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#1E7E4E', marginTop: 6 }}>
                <span>Total ITC (claimable against output GST)</span>
                <span>{fmt(totalITC)}</span>
              </div>
            )}
          </div>

          {/* For CA export hint */}
          <div className="card" style={{ padding: '14px 20px', background: 'var(--surface2)', fontSize: 12, color: 'var(--text2)' }}>
            <strong style={{ color: 'var(--text1)' }}>For your CA:</strong> Export monthly P&L from the Reports page → Excel. The Excel includes all expense categories, ITC amounts, and payroll totals. GST summary (Output − ITC = Net payable) is available in the Quarterly GST section on the Dashboard.
          </div>
        </div>
      )}

      {/* ── TAB: Recurring ────────────────────────────────────── */}
      {tab === 'recurring' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button className="btn-primary" onClick={() => { setEditRecurring(null); setShowRecurring(true); }}>+ Add Recurring</button>
          </div>
          {recurring.length === 0 && (
            <div className="card empty" style={{ padding: 40, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🔁</div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>No recurring expenses yet</div>
              <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>Set up recurring templates for internet, software subscriptions etc. — apply them to any month with one click</div>
              <button className="btn-primary" onClick={() => setShowRecurring(true)}>+ Add first recurring</button>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {recurring.map(r => (
              <div key={r.id} className="card" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 20, width: 32, textAlign: 'center' }}>{r.category_icon || '📦'}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text2)' }}>
                    {r.frequency} · {r.category_name || 'Uncategorised'}
                    {r.vendor && ` · ${r.vendor}`}
                    {r.payment_method && ` · ${r.payment_method}`}
                    · from {r.start_month}
                    {r.end_month && ` to ${r.end_month}`}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontWeight: 700, color: '#C0392B' }}>{fmt(parseFloat(r.amount))}</div>
                  {parseFloat(r.gst_amount) > 0 && <div style={{ fontSize: 11, color: '#1E7E4E' }}>ITC: {fmt(parseFloat(r.gst_amount))}</div>}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button className="btn-ghost btn-sm" onClick={() => openMonthPicker(r)}
                    title="Choose which months to apply this to">
                    + {month.slice(5,7)}/{month.slice(0,4)}
                  </button>
                  <button className="btn-ghost btn-sm" onClick={() => { setEditRecurring(r); setShowRecurring(true); }}>Edit</button>
                  <button className="btn-ghost btn-sm" style={{ color: 'var(--red)' }}
                    onClick={() => { if(window.confirm('Remove this recurring template?')) deleteRecurringMutation.mutate(r.id); }}>
                    Del
                  </button>
                </div>
              </div>
            ))}
          </div>

          {recurring.length > 0 && (
            <div style={{ marginTop: 16, fontSize: 12, color: 'var(--text2)', background: 'var(--surface2)', padding: '10px 14px', borderRadius: 8 }}>
              Click "+ MM/YYYY" on any recurring expense to add it to this month's list. Each template stays active until you remove it or set an end month.
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {showAdd && (
        <ExpenseModal categories={categories} onClose={() => setShowAdd(false)}
          onSave={data => addMutation.mutate(data)}
          onSaveAndRecur={handleSaveAndRecur} />
      )}
      {editing && (
        <ExpenseModal expense={editing} categories={categories} onClose={() => setEditing(null)}
          onSave={data => editMutation.mutate({ id: editing.id, data })}
          onSaveAndRecur={handleSaveAndRecur} />
      )}
      {showRecurring && (
        <RecurringModal template={editRecurring} categories={categories} onClose={() => { setShowRecurring(false); setEditRecurring(null); }}
          onSave={data => editRecurring ? editRecurringMutation.mutate({ id: editRecurring.id, data }) : addRecurringMutation.mutate(data)} />
      )}

      {/* Multi-month apply picker */}
      {monthPicker && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'var(--surface)', borderRadius:12, padding:28, maxWidth:380, width:'90%', boxShadow:'0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ fontWeight:700, fontSize:16, marginBottom:4 }}>Apply to months</div>
            <div style={{ color:'var(--text-muted)', fontSize:13, marginBottom:16 }}>{monthPicker.name} · ₹{parseFloat(monthPicker.amount).toLocaleString('en-IN')}</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:20 }}>
              {fyMonths.map(m => {
                const alreadyDone = appliedMonths.includes(m);
                const checked = pickedMonths.has(m);
                const isFuture = m > new Date().toISOString().slice(0,7);
                const label = new Date(m + '-02').toLocaleString('en-IN', { month:'short', year:'numeric' });
                return (
                  <label key={m} style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 10px', borderRadius:8,
                    background: alreadyDone ? 'var(--bg-muted, #f0f0f0)' : checked ? 'var(--primary-light, #e8f0fe)' : 'var(--bg)',
                    border: `1px solid ${alreadyDone ? 'var(--border)' : checked ? 'var(--primary)' : 'var(--border)'}`,
                    cursor: alreadyDone ? 'not-allowed' : 'pointer',
                    fontSize:14, fontWeight: checked ? 600 : 400, opacity: alreadyDone ? 0.6 : 1 }}>
                    {alreadyDone
                      ? <span style={{ width:15, height:15, display:'inline-flex', alignItems:'center', justifyContent:'center', color:'#1E7E4E', fontSize:14 }}>✓</span>
                      : <input type="checkbox" checked={checked} onChange={e => {
                          const s = new Set(pickedMonths);
                          e.target.checked ? s.add(m) : s.delete(m);
                          setPickedMonths(s);
                        }} style={{ accentColor:'var(--primary)', width:15, height:15 }} />
                    }
                    {label}
                    {alreadyDone && <span style={{ fontSize:10, color:'#1E7E4E', marginLeft:'auto' }}>done</span>}
                    {!alreadyDone && isFuture && <span style={{ fontSize:10, color:'var(--primary)', marginLeft:'auto' }}>advance</span>}
                  </label>
                );
              })}
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button className="btn-primary" style={{ flex:1 }} disabled={pickedMonths.size === 0} onClick={applyToPickedMonths}>
                Apply to {pickedMonths.size} month{pickedMonths.size !== 1 ? 's' : ''}
              </button>
              <button className="btn-ghost" onClick={() => setMonthPicker(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
